/**
 * GRIDHEIMR -- Bot Chat Component
 *
 * Interactive chat interface for sending messages to a running bot.
 * Messages are delivered via Docker exec and responses captured synchronously.
 */

import React, { useEffect, useRef, useState } from "react";
import { sendBotMessage } from "../api/bots";
import { getErrorMessage } from "../api/client";
import type { ChatMessage } from "../api/types";
import { BotStatus } from "../api/types";
import { BLUE, YELLOW, DARK_BG, PIXEL, MONO, panel, inputField } from "../theme";

interface BotChatProps {
  botId: string;
  botStatus: BotStatus;
}

export function BotChat({ botId, botStatus }: BotChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isRunning = botStatus === BotStatus.RUNNING;

  /** Auto-scroll to bottom when messages change */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /** Focus input when component mounts */
  useEffect(() => {
    if (isRunning) {
      inputRef.current?.focus();
    }
  }, [isRunning]);

  /** Send a message to the bot */
  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending || !isRunning) return;

    setError(null);
    setInput("");
    setSending(true);

    const userMsg: ChatMessage = {
      role: "user",
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const resp = await sendBotMessage(botId, trimmed);

      const assistantMsg: ChatMessage = {
        role: "assistant",
        content: resp.response || "(empty response)",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      if (resp.exit_code !== 0) {
        setError(`Command exited with code ${resp.exit_code}`);
      }
    } catch (err) {
      const errMsg = getErrorMessage(err);
      setError(errMsg);

      const errorAssistantMsg: ChatMessage = {
        role: "assistant",
        content: `Error: ${errMsg}`,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorAssistantMsg]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  /** Handle Enter key */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isRunning) {
    return (
      <div style={styles.panel}>
        <h3 style={styles.title}>// CHAT</h3>
        <p style={styles.disabledText}>
          Bot must be running to chat. Start the bot to enable messaging.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <h3 style={styles.title}>// CHAT</h3>

      {/* Messages area */}
      <div style={styles.messagesContainer}>
        {messages.length === 0 && (
          <div style={styles.emptyState}>
            Send a message to start chatting with your bot.
          </div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              ...styles.messageBubble,
              ...(msg.role === "user" ? styles.userBubble : styles.assistantBubble),
            }}
          >
            <div style={styles.messageRole}>
              {msg.role === "user" ? "you" : "bot"}
            </div>
            <div style={styles.messageContent}>
              {msg.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ ...styles.messageBubble, ...styles.assistantBubble }}>
            <div style={styles.messageRole}>bot</div>
            <div style={styles.thinkingText}>Thinking...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error display */}
      {error && (
        <div style={styles.errorBar}>
          {error}
        </div>
      )}

      {/* Input area */}
      <div style={styles.inputRow}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={sending ? "Waiting for response..." : "Type a message..."}
          disabled={sending}
          style={{
            ...styles.input,
            opacity: sending ? 0.5 : 1,
          }}
        />
        <button
          onClick={handleSend}
          disabled={sending || !input.trim()}
          style={{
            ...styles.sendButton,
            opacity: sending || !input.trim() ? 0.4 : 1,
          }}
        >
          {sending ? "..." : "SEND"}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    ...panel,
  },
  title: {
    fontFamily: MONO,
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.3)",
    letterSpacing: "0.15em",
    margin: "0 0 1rem 0",
    fontWeight: 400,
  },
  disabledText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.3)",
    margin: 0,
  },
  messagesContainer: {
    maxHeight: "400px",
    overflowY: "auto",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  emptyState: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.25)",
    textAlign: "center",
    padding: "2rem 0",
  },
  messageBubble: {
    padding: "0.75rem 1rem",
    maxWidth: "85%",
    wordBreak: "break-word",
  },
  userBubble: {
    alignSelf: "flex-end",
    borderLeft: "none",
    borderRight: `2px solid ${YELLOW}`,
    background: "rgba(254,204,0,0.05)",
  },
  assistantBubble: {
    alignSelf: "flex-start",
    borderLeft: `2px solid ${BLUE}`,
    borderRight: "none",
    background: "rgba(0,106,167,0.08)",
  },
  messageRole: {
    fontFamily: PIXEL,
    fontSize: "0.35rem",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: "0.35rem",
  },
  messageContent: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.8)",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  thinkingText: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.4)",
    fontStyle: "italic",
  },
  errorBar: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "#ef4444",
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.3)",
    padding: "0.5rem 0.75rem",
    marginBottom: "0.75rem",
  },
  inputRow: {
    display: "flex",
    gap: "0.5rem",
    alignItems: "stretch",
  },
  input: {
    ...inputField,
    flex: 1,
  },
  sendButton: {
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    padding: "10px 20px",
    background: YELLOW,
    color: "#000",
    border: "none",
    cursor: "pointer",
    letterSpacing: "0.1em",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },
};
